import { useEffect, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';

import useValidDeliveryDaysQuery from 'api/queries/useValidDeliveryDaysNoSubQuery';
import { OrderMinInfoModal } from 'features/signup/OrderMinInfoModal';
import { PlanRangeInfoModal } from 'features/signup/PlanRangeInfoModal';
import { SignUpFeeDetailsModal } from 'features/signup/SignUpFeeDetailsModal';
import Card from 'honeydew/Card';
import { Divider } from 'honeydew/Divider';
import { getSelectedDeliveryDay, getSignupLocalStorage, setSignupLocalStorage } from 'utils/signupUtils';

function WhatYoullGetModule() {
    const signupLocalStorage = getSignupLocalStorage();

    const [feeDetailsModal, setFeeDetailsModal] = useState(false);
    const [planRangeInfoModal, setPlanRangeInfoModal] = useState(false);
    const [orderMinInfoModal, setOrderMinInfoModal] = useState(false);

    const { data: validDeliveryDays } = useValidDeliveryDaysQuery({
        extendedQueryKeys: ['signup'],
        zip: signupLocalStorage?.zip,
    });

    const selectedDeliveryDay = getSelectedDeliveryDay({ validDeliveryDays });
    const { freeShippingThreshold, orderMinimum, prefQuizRange } = selectedDeliveryDay ?? {};
    const { maxRange: prefQuizMaxRange = 0, minRange: prefQuizMinRange = 0 } = prefQuizRange ?? {};

    const minRange = Math.floor(prefQuizMinRange);
    const maxRange = Math.ceil(prefQuizMaxRange);

    useEffect(() => {
        setSignupLocalStorage({ maxRange, minRange });
    }, [minRange, maxRange]);

    const handleOpenFeeDetailsModal = () => {
        setFeeDetailsModal(true);
    };

    return (
        <Card extendedStyles={{ padding: 'px-20 py-25' }} data-optimizely-id="what-youll-get">
            <p className="mb-10 font-grotesk-bold">
                We&apos;ll start your cart with{' '}
                <button type="button" aria-label="Open plan range modal" onClick={() => setPlanRangeInfoModal(true)}>
                    <InformationCircleIcon width="18px" className="-mb-[4px]" />
                </button>
            </p>
            <p>
                ${minRange}-{maxRange}* of groceries
            </p>
            <p className="mt-15">Add or remove anything you like</p>
            <p className="mb-10 mt-15 font-grotesk-bold">
                Your order minimum{' '}
                <button type="button" aria-label="Open order min modal" onClick={() => setOrderMinInfoModal(true)}>
                    <InformationCircleIcon width="18px" className="-mb-[4px]" />
                </button>
            </p>
            <p>${orderMinimum}</p>

            {freeShippingThreshold && (
                <>
                    <div>
                        <p className="mb-10 mt-15 font-grotesk-bold">
                            Free shipping on orders ${freeShippingThreshold}+
                        </p>
                        <p>(not including taxes or applicable fees)</p>
                    </div>

                    <Divider />
                </>
            )}

            <p className="text-body-xs mt-20 text-grey">
                *Taxes, shipping and fees may apply.{' '}
                <button type="button" className="underline" onClick={handleOpenFeeDetailsModal}>
                    Learn more
                </button>
            </p>

            <SignUpFeeDetailsModal open={feeDetailsModal} setOpen={() => setFeeDetailsModal(false)} />
            <PlanRangeInfoModal
                open={planRangeInfoModal}
                planMaximum={maxRange}
                planMinimum={minRange}
                setOpen={setPlanRangeInfoModal}
            />
            <OrderMinInfoModal open={orderMinInfoModal} orderMinimum={orderMinimum} setOpen={setOrderMinInfoModal} />
        </Card>
    );
}

export function WhatYoullGet({ card }) {
    return card ? (
        <div className="mb-20">
            <h3 className="text-body-lg mb-10 font-grotesk-bold">What you’ll get</h3>
            <WhatYoullGetModule card />
        </div>
    ) : (
        <WhatYoullGetModule />
    );
}

WhatYoullGet.propTypes = {
    card: PropTypes.bool,
};

WhatYoullGet.defaultProps = {
    card: false,
};
